import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICheckSettlementPeriodsParams,
  ICheckSettlementPeriodsResponse,
  ICreateLabel,
  IDeleteDefaultContractParams,
  IEditLabel,
  ISetDefaultContractParams,
  IGetLabelListInfiniteParams,
  IGetLabelTopProductsParams,
  ILabelEarningsResponse,
  ILabelPageResponse,
  ILabelResponse,
  ILabelTopProduct,
  ILabelTopProductsResponse,
  IUpdateLabelByTokenParams,
  IValidateLabelAccountParams,
} from './types';
import { handleErrorsResponse } from 'utils/service/errorHandler';

export const useGetLabelListInfinite = (
  params: IGetLabelListInfiniteParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<ILabelResponse[], DefaultError>({
    queryKey: ['labels', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Label/Search',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<ILabelPageResponse>(response, {
        groupName: `useGetLabelListInfinite - ${pageParam}`,
      });

      return infiniteHandler<ILabelResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetLabel = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ILabelResponse, DefaultError>({
    queryKey: ['label', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Label/${id}`,
      });

      return responseHandler(response, {
        groupName: `usetGetLabel - ${id}`,
        notFoundPath: '/label',
      });
    },
    enabled: enabled,
  });
};

export const useGetLabelByToken = (token: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ILabelResponse, DefaultError>({
    queryKey: ['label-by-token', token],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Label/ByToken',
        params: {
          token,
        },
      });

      return responseHandler(response, {
        groupName: `useGetLabelByToken - ${token}`,
      });
    },
    enabled: enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });
};

export const useUpdateLabelByToken = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success, failure } = useQueryToast('useUpdateLabelByToken');

  return useMutation<void, DefaultError, IUpdateLabelByTokenParams>({
    mutationFn: async (body) => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('label.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: 'Label/UpdateByToken',
        body,
      });

      return responseHandler(response, {
        groupName: 'useUpdateLabelByToken',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('label.what'),
        })
      );
    },
    onError: (error) => {
      dismiss();
      try {
        const { toasts } = handleErrorsResponse(error.message);
        if (toasts.length) {
          toasts.forEach(message => failure(message));
        } else {
          failure(
            i18next.t('service-message.update.failure', {
              what: i18next.t('label.what'),
            })
          );
        }
      } catch {
        failure(
          i18next.t('service-message.update.failure', {
            what: i18next.t('label.what'),
          })
        );
      }
    },
  });
};

export const usePostLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePostLabel');
  return useMutation<ILabelResponse, DefaultError, ICreateLabel>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.labelName ?? i18next.t('label.what'),
        })
      );

      const response = await apiClient({ method: 'POST', url: 'Label', body });

      return responseHandler(response, {
        groupName: 'usePostLabel',
        notFoundPath: '/label',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.labelName ?? i18next.t('label.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutLabel');

  return useMutation<ILabelResponse, DefaultError, IEditLabel>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: body.labelName ?? i18next.t('label.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'Label',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutLabel',
        notFoundPath: '/label',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: data.labelName ?? i18next.t('label.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutLabelSetDefaultContract = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutLabelSetDefaultContract'
  );

  return useMutation<void, DefaultError, ISetDefaultContractParams>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('label.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'Label/SetDefaultContract',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutLabelSetDefaultContract',
        notFoundPath: '/label',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('label.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutLabelDeleteDefaultContract = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutLabelDeleteDefaultContract'
  );

  return useMutation<void, DefaultError, IDeleteDefaultContractParams>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: i18next.t('label.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: 'Label/DeleteDefaultContract',
        body,
      });

      return responseHandler(response, {
        groupName: 'usePutLabelDeleteDefaultContract',
        notFoundPath: '/label',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('label.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useDeleteLabel');

  return useMutation<void, DefaultError, string>({
    mutationFn: async id => {
      loading(
        i18next.t('service-message.delete.loading', {
          what: i18next.t('label.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Label/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteLabel',
        notFoundPath: '/label',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.delete.success', {
          what: i18next.t('label.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useCheckSettlementPeriods = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<
    ICheckSettlementPeriodsResponse,
    DefaultError,
    ICheckSettlementPeriodsParams
  >({
    mutationFn: async params => {
      const response = await apiClient({
        method: 'GET',
        url: 'Settlement/Check',
        params: params,
      });

      return responseHandler(response, {
        groupName: 'useCheckSettlementPeriods',
      });
    },
  });
};

export const useGetAllVcfLabels = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<void, DefaultError, void>({
    mutationFn: async () => {
      const response = await apiClient({
        method: 'GET',
        url: 'Label/Vcf/All',
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contacts.vcf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return responseHandler(response, {
        groupName: 'useGetAllVcfLabels',
      });
    },
  });
};

export const useGetVcfLabel = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<void, DefaultError, string>({
    mutationFn: async (labelId: string) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Label/Vcf',
        params: {
          LabelId: labelId
        }
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contact-${labelId}.vcf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return responseHandler(response, {
        groupName: 'useGetVcfLabel',
      });
    },
  });
};

export const useGetLabelTopProductsInfinite = (
  params: IGetLabelTopProductsParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<ILabelTopProduct[], DefaultError>({
    queryKey: ['label-top-products', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statistics/Top/Products/Label',
        params: {
          labelId: params.labelId,
          startSettlementPeriodId: params.startSettlementPeriodId,
          endSettlementPeriodId: params.endSettlementPeriodId,
          itemCount,
          page: pageParam,
        },
      });

      const value = await responseHandler<ILabelTopProductsResponse>(response, {
        groupName: `useGetLabelTopProductsInfinite - ${pageParam}`,
      });

      return infiniteHandler<ILabelTopProduct>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    enabled: !!params.labelId && !!params.startSettlementPeriodId && !!params.endSettlementPeriodId,
  });
};

export const useGetLabelEarnings = (
  startSettlementPeriodId: string,
  endSettlementPeriodId: string,
  productId: string,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ILabelEarningsResponse, DefaultError>({
    queryKey: ['label-earnings', startSettlementPeriodId, endSettlementPeriodId, productId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'MusicProductDigital/Earnings',
        params: {
          startSettlementPeriodId,
          endSettlementPeriodId,
          productId,
        },
      });

      return responseHandler<ILabelEarningsResponse>(response, {
        groupName: 'useGetLabelEarnings',
        onNotFound: () => {},
      });
    },
    enabled: enabled && !!startSettlementPeriodId && !!endSettlementPeriodId && !!productId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useBanLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useBanLabel');

  return useMutation<void, DefaultError, { labelId: string; withdrawAllProducts: boolean }>({
    mutationFn: async ({ labelId, withdrawAllProducts }) => {
      loading(
        i18next.t('service-message.ban.loading', {
          what: i18next.t('label.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: 'Label/Ban',
        params: {
          LabelId: labelId,
          WithdrawAllProducts: withdrawAllProducts,
        },
      });

      return responseHandler(response, {
        groupName: 'useBanLabel',
        notFoundPath: '/label',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.ban.success', {
          what: i18next.t('label.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useValidateLabelAccount = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, failure } = useQueryToast('useValidateLabelAccount');

  return useMutation<boolean, DefaultError, IValidateLabelAccountParams>({
    mutationFn: async (body) => {
      loading(
        i18next.t('service-message.validate.loading', {
          what: i18next.t('label.account.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: 'Label/ValidateAccount',
        body,
      });

      return responseHandler<boolean>(response, {
        groupName: 'useValidateLabelAccount',
        notFoundPath: '/label',
      });
    },
    onSuccess: () => {
      dismiss();
    },
    onError: (error) => {
      dismiss();
      try {
        const { toasts } = handleErrorsResponse(error.message);
        if (toasts.length) {
          toasts.forEach(message => failure(message));
        } else {
          failure(
            i18next.t('service-message.update.failure', {
              what: i18next.t('label.account.what'),
            })
          );
        }
      } catch {
        failure(
          i18next.t('service-message.update.failure', {
            what: i18next.t('label.account.what'),
          })
        );
      }
    },
  });
};

export const useSendUpdateLabelEmail = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success, failure } = useQueryToast('useSendUpdateLabelEmail');

  return useMutation<void, DefaultError, string>({
    mutationFn: async (id) => {
      loading(
        i18next.t('service-message.send.loading', {
          what: i18next.t('label.update-email.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Label/${id}/SendUpdateLabelEmail`,
      });

      return responseHandler(response, {
        groupName: 'useSendUpdateLabelEmail',
        notFoundPath: '/label',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.send.success', {
          what: i18next.t('label.update-email.what'),
        })
      );
    },
    onError: (error) => {
      dismiss();
      try {
        const { toasts } = handleErrorsResponse(error.message);
        if (toasts.length) {
          toasts.forEach(message => failure(message));
        } else {
          failure(
            i18next.t('service-message.send.failure', {
              what: i18next.t('label.update-email.what'),
            })
          );
        }
      } catch {
        failure(
          i18next.t('service-message.send.failure', {
            what: i18next.t('label.update-email.what'),
          })
        );
      }
    },
  });
};
